import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "../../../Assets/Logo_Web.png";
import { FaBars, FaTimes, FaHome, FaInfoCircle, FaBlog, FaQuestionCircle, FaEnvelope, FaPaw } from "react-icons/fa";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 784);

  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handlePartnerClick = (): void => {
    navigate("/partner");
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const handleResize = (): void => {
      const mobile = window.innerWidth < 784;
      setIsMobile(mobile);
      if (!mobile) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isActive = (path: string): boolean => {
    // Remove trailing slashes and compare
    const currentPath = location.pathname.replace(/\/+$/, "");
    const comparePath = path.replace(/\/+$/, "");
    return currentPath === comparePath;
  };

  return (
    <>
      {/* Desktop Navbar */}
      {!isMobile && (
        <nav className="bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg border-b border-slate-200 fixed w-full top-0 z-10">
          <div className="w-full px-6 lg:px-12 xl:px-20 2xl:px-32">
            <div className="flex items-center justify-between h-16 w-full">

              {/* Logo */}
              <div className="flex-shrink-0 transition-transform duration-200 hover:scale-105">
                <img className="h-10" src={Logo} alt="Logo" />
              </div>

              {/* Navigation Links */}
              <div className="hidden md:flex items-center space-x-8">
                <Link
                  to="/puphub"
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive("/puphub")
                      ? "text-blue-800 text-sm font-semibold rounded-lg "
                      : "text-black hover:text-gray-600"
                  }`}
                >
                  Home
                </Link>

                <Link
                  to="/aboutus"
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive("/aboutus")
                      ? "text-blue-800 text-sm font-semibold rounded-lg "
                      : "text-black hover:text-gray-600"
                  }`}
                >
                  About Us
                </Link>

                <Link
                  to="/blogs"
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive("/blogs")
                      ? "text-blue-800 text-sm font-semibold rounded-lg "
                      : "text-black hover:text-gray-600"
                  }`}
                >
                  Blogs
                </Link>

                <Link
                  to="/FAQ"
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive("/FAQ")
                      ? "text-blue-800 text-sm font-semibold rounded-lg "
                      : "text-black hover:text-gray-600"
                  }`}
                >
                  FAQ
                </Link>

                <Link
                  to="/contactus"
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive("/contactus")
                      ? "text-blue-800 text-sm font-semibold rounded-lg "
                      : "text-black hover:text-gray-600"
                  }`}
                >
                  Contact Us
                </Link>

                <button
                  onClick={handlePartnerClick}
                  className="ml-4 px-5 py-2.5 text-white text-sm font-semibold rounded-lg shadow-md 
                    bg-gradient-to-r from-indigo-600 to-purple-600 
                    hover:from-indigo-500 hover:to-purple-500 hover:shadow-lg
                    transform hover:scale-105 active:scale-95
                    transition-all duration-200 ease-out 
                    focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2
                    border border-indigo-500 hover:border-indigo-400"
                >
                  <span className="flex items-center">
                    <FaPaw className="mr-2" />
                    Visit PupHub Partner
                  </span>
                </button>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Mobile Menu Button */}
      {isMobile && (
        <button
          className="fixed top-4 left-4 z-50 p-3 rounded-full bg-white shadow-md hover:shadow-lg transition duration-300 ease-in-out focus:outline-none"
          onClick={toggleMenu}
          aria-label="Menu"
        >
          {isMenuOpen ? (
            <FaTimes className="w-6 h-6 text-gray-800 hover:text-red-500 transition duration-300" />
          ) : (
            <FaBars className="w-6 h-6 text-gray-800 hover:text-blue-500 transition duration-300" />
          )}
        </button>
      )}

      {/* Mobile Side Menu */}
      {isMobile && (
        <>
          <div
            className={`fixed top-0 left-0 w-72 h-full bg-gradient-to-b from-blue-50 to-indigo-50 shadow-2xl border-r border-slate-200 z-40 transform 
              ${isMenuOpen ? "translate-x-0" : "-translate-x-full"} 
              transition-transform duration-300 ease-out`}
          >
            <div className="flex justify-center items-center pt-8 pb-4 border-b border-gray-200">
              <img src={Logo} alt="PupHub Logo" className="h-10 ml-14" />
            </div>

            <div className="flex flex-col p-4 space-y-3">
              <Link
                to="/puphub"
                onClick={toggleMenu}
                className={`flex items-center px-4 py-3 transition-colors duration-200 ${
                  isActive("/puphub")
                    ? "text-blue-950 text-sm font-semibold rounded-lg "
                    : "text-black hover:text-gray-600"
                }`}
              >
                <FaHome className={`mr-3 text-lg ${
                  isActive("/puphub") ? "text-blue-800" : "text-gray-500"
                }`} />
                <span className="font-medium">Home</span>
              </Link>

              <Link
                to="/aboutus"
                onClick={toggleMenu}
                className={`flex items-center px-4 py-3 transition-colors duration-200 ${
                  isActive("/aboutus")
                    ? "text-blue-950 text-sm font-semibold rounded-lg "
                    : "text-black hover:text-gray-600"
                }`}
              >
                <FaInfoCircle className={`mr-3 text-lg ${
                  isActive("/aboutus") ? "text-blue-800" : "text-gray-500"
                }`} />
                <span className="font-medium">About Us</span>
              </Link>

              <Link
                to="/blogs"
                onClick={toggleMenu}
                className={`flex items-center px-4 py-3 transition-colors duration-200 ${
                  isActive("/blogs")
                    ? "text-blue-950 text-sm font-semibold rounded-lg "
                    : "text-black hover:text-gray-600"
                }`}
              >
                <FaBlog className={`mr-3 text-lg ${
                  isActive("/blogs") ? "text-blue-800" : "text-gray-500"
                }`} />
                <span className="font-medium">Blogs</span>
              </Link>

              <Link
                to="/FAQ"
                onClick={toggleMenu}
                className={`flex items-center px-4 py-3 transition-colors duration-200 ${
                  isActive("/FAQ")
                    ? "text-blue-950 text-sm font-semibold rounded-lg "
                    : "text-black hover:text-gray-600"
                }`}
              >
                <FaQuestionCircle className={`mr-3 text-lg ${
                  isActive("/FAQ") ? "text-blue-800" : "text-gray-500"
                }`} />
                <span className="font-medium">FAQ</span>
              </Link>

              <Link
                to="/contactus"
                onClick={toggleMenu}
                className={`flex items-center px-4 py-3 transition-colors duration-200 ${
                  isActive("/contactus")
                    ? "text-blue-950 text-sm font-semibold rounded-lg "
                    : "text-black hover:text-gray-600"
                }`}
              >
                <FaEnvelope className={`mr-3 text-lg ${
                  isActive("/contactus") ? "text-blue-800" : "text-gray-500"
                }`} />
                <span className="font-medium">Contact Us</span>
              </Link>

              <div className="pt-4 border-t border-indigo-700 mt-4">
                <button
                  onClick={handlePartnerClick}
                  className="w-full px-4 py-3.5 text-white font-semibold rounded-xl shadow-md 
                    bg-gradient-to-r from-indigo-600 to-purple-600 
                    hover:from-indigo-500 hover:to-purple-500 hover:shadow-lg
                    transform hover:scale-105 active:scale-95
                    transition-all duration-200 ease-out 
                    flex items-center justify-center
                    focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2
                    border border-indigo-500 hover:border-indigo-400"
                >
                  <FaPaw className="mr-2" />
                  <span>Visit PupHub Partner</span>
                </button>
              </div>
            </div>
          </div>

          {/* Overlay */}
          {isMenuOpen && (
            <div
              className="fixed inset-0 bg-slate-900 bg-opacity-50 z-30 transition-opacity duration-300 backdrop-blur-sm"
              onClick={toggleMenu}
            />
          )}
        </>
      )}
    </>
  );
};

export default Navbar;